import { createContext, useContext, useEffect, useState } from "react";
import { AuthProviderProps } from "./types";
import { authServiceSupabase } from "../supabase/authService";
import { supabase } from "../supabase/supabaseClient";
import { useClearStorageOnDeploy } from "../../hooks/useClearStorageOnDeploy";

const AuthContext = createContext<AuthProviderProps | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoadingApp, setIsLoadingApp] = useState(true);
  const supabaseAuthService = authServiceSupabase;

  /**
   * TODO: Delete hook if not needed after migration to supabase
   */
  useClearStorageOnDeploy("1.0.1");

  useEffect(() => {
    // Modern approach: First get the session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setCurrentUser(session?.user ?? null);
      setIsLoadingApp(false);
    });

    // Then set up the auth state change listener
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      // Only update state if there's an actual change
      setCurrentUser(prevUser => {
        const newUser = session?.user ?? null;

        // If both are null or have the same ID, don't update
        if ((!prevUser && !newUser) || prevUser?.id === newUser?.id) {
          return prevUser;
        }

        return newUser;
      });
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const handleSignIn = async (email: string, password: string) => {
    setIsLoadingApp(true);
    try {
      const result = await supabaseAuthService.signIn(email, password);
      setCurrentUser(result.user);
      return result;
    } catch (error) {
      console.error("Sign in failed:", error);
      throw error;
    } finally {
      setIsLoadingApp(false);
    }
  };

  const handleSignUp = async (email: string, password: string) => {
    setIsLoadingApp(true);
    try {
      const result = await supabaseAuthService.signUp(email, password);
      setCurrentUser(result.user);
      return result;
    } catch (error) {
      console.error("Sign up failed:", error);
      throw error;
    } finally {
      setIsLoadingApp(false);
    }
  };

  const handleSignOut = async () => {
    setIsLoadingApp(true);
    try {
      const result = await supabaseAuthService.signOut();
      setCurrentUser(null);
      return result;
    } catch (error) {
      console.error("Sign out failed:", error);
      throw error;
    } finally {
      setIsLoadingApp(false);
    }
  };

  const value = {
    // Get Supabase access token instead of MongoDB token
    getAccessToken: async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      return session?.access_token || "";
    },

    refreshToken: async () => {
      const {
        data: { session },
      } = await supabase.auth.refreshSession();
      return session?.access_token || null;
    },

    isSignedIn: !!currentUser,
    isLoadingApp,
    signIn: handleSignIn,
    signUp: handleSignUp,
    signOut: handleSignOut,
    user: {
      id: currentUser?.id || "",
      email: currentUser?.email || "",
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
